import { Button, BUTTON_TYPES } from '../../components/Button'
import { BTN_TXT, TOptionsWithValueLabelResponse } from '../../constants'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { Table } from '../../components/Table'
import { columns } from './constants'
import {
  TAdditionalValue,
  TAdditionalValuesParams,
  TAdditionalValuesTableRequestData
} from './types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DEFAULT_PAGE } from '../../components/Table/constants'
import { setLoading } from '../../redux/store/common/slice'
import { useDispatch, useSelector } from 'react-redux'
import {
  getAdditionalValuesSubTypesList,
  getAdditionalValuesTable,
  getAdditionalValuesTypesList
} from '../Dashboard/api'
import { DeleteModal } from './DeleteModal'
import { useAdditionalValuesPopup } from './Providers/AdditionalValuesPopupProvider'
import { AddEditAdditionalValueModal } from './AddEditAdditionalValueModal/AddEditAdditionalValueModal'
import { useAdditionalValuesContext } from './Providers/AdditionalValuesContextProvider'
import { getUser } from 'redux/store/user/getters'

export const AdditionalValuesContainer = () => {
  const dispatch = useDispatch()
  const { addEditAdditionalValuesPopup } = useAdditionalValuesPopup()
  const { isAbleToManage } = useAdditionalValuesContext().state
  const [typesList, setTypesList] = useState<TOptionsWithValueLabelResponse[]>(
    []
  )
  const [subtypesList, setSubTypesList] = useState<
    TOptionsWithValueLabelResponse[]
  >([])

  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [tableData, setTableData] = useState<TAdditionalValue[]>([])
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [sortParams, setSortParams] =
    useState<TAdditionalValuesTableRequestData>({})

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const user = useSelector(getUser)
  const canSeeHealthSystem = !user.outside_community

  const getSubtypeList = async () => {
    await getAdditionalValuesSubTypesList().then((res) => {
      setSubTypesList(
        res.data.results.map((option) => ({
          label: option.value,
          value: option.id
        }))
      )
    })
  }
  const getTypeList = async () => {
    await getAdditionalValuesTypesList().then((res) => {
      setTypesList(
        res.data.results.map((option) => ({
          label: option.value,
          value: option.id
        }))
      )
    })
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getTableData({ ...sortParams, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
        getTypeList()
        getSubtypeList()
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const getTableData = useCallback(
    ({ sortField, sortOrder, page }: TAdditionalValuesTableRequestData) => {
      dispatch(setLoading(true))
      const params: TAdditionalValuesParams = {}
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ? page : pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      getAdditionalValuesTable({ params })
        .then((resp) => {
          if (!resp.data?.results) {
            return
          }
          setTableData(resp.data.results)
          setTotalNumber(resp.data.count)
          if (firstLoad) {
            setFirstLoad(false)
          }
        })
        .finally(() => dispatch(setLoading(false)))
    },
    [dispatch, firstLoad, pageInfo]
  )

  const handleCreate = () => {
    addEditAdditionalValuesPopup.actions.open()
  }

  const onAddCallback = useCallback(() => {
    getTableData({
      ...sortParams,
      page: { ...pageInfo }
    })
  }, [getTableData, pageInfo, sortParams])

  return (
    <div className="additional-values-container full-width">
      {isAbleToManage && (
        <div className="row justify-end">
          <Button
            type={BUTTON_TYPES.PRIMARY}
            icon={<Plus16 />}
            onClick={handleCreate}
            upperCase
          >
            {BTN_TXT.ADD_ADDITIONAL_VALUE}
          </Button>
        </div>
      )}

      <Table<TAdditionalValue>
        dataSource={tableData}
        onChange={handleTableChange}
        className="hospitals-list-table mt-4"
        columns={columns({
          typesList,
          subtypesList,
          isAbleToManage,
          user,
          canSeeHealthSystem
        })}
        onChangePage={handleChangePageSize}
        rowKey={(row) => row?.uuid}
        scroll={{ scrollToFirstRowOnChange: true, x: 'max-content' }}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        createButton={
          isAbleToManage
            ? {
                buttonText: BTN_TXT.ADD_ADDITIONAL_VALUE,
                availability: true,
                action: handleCreate
              }
            : undefined
        }
      />
      <DeleteModal callback={onAddCallback} />
      <AddEditAdditionalValueModal onSaveCallback={onAddCallback} />
    </div>
  )
}
